import { isWorkspacePath, type FileEntry, type Revision } from '@p2b/contract';
import type { PublicationView } from '@p2b/sdk';
import { fill } from '../../i18n.js';

/* Pure helpers of the project page, kept apart from React and the API client so tests can load them. */

export interface Folder {
  name: string;
  path: string;
  folders: Map<string, Folder>;
  files: FileEntry[];
  count: number;
}

export function buildTree(files: FileEntry[]): Folder {
  const root: Folder = { name: '', path: '', folders: new Map(), files: [], count: 0 };
  for (const f of files) {
    const parts = f.path.split('/');
    let node = root;
    node.count++;
    for (let i = 0; i < parts.length - 1; i++) {
      const name = parts[i]!;
      let next = node.folders.get(name);
      if (!next) {
        next = { name, path: `${node.path}${name}/`, folders: new Map(), files: [], count: 0 };
        node.folders.set(name, next);
      }
      next.count++;
      node = next;
    }
    node.files.push(f);
  }
  return root;
}

/** The folder at a path ("a/b/"), or null when the tree no longer has it. */
export function folderAt(root: Folder, path: string): Folder | null {
  let node: Folder | undefined = root;
  for (const part of path.split('/').filter(Boolean)) node = node?.folders.get(part);
  return node ?? null;
}

/** The workspace's own files: kind `workspace`, or older ones pushed as `other` (CLAUDE.md, LOG.md, dotfiles). */
export function isWorkspaceFile(f: Pick<FileEntry, 'kind' | 'path'>): boolean {
  return f.kind === 'workspace' || (f.kind === 'other' && isWorkspacePath(f.path));
}

export type DeliveryState = 'delivered' | 'withdrawn' | 'frozen';

export interface DeliveryRow {
  rev: Revision;
  /** Publications of this revision in force, newest first. */
  active: PublicationView[];
  withdrawn: PublicationView[];
  state: DeliveryState;
}

/** One row per revision: delivered when a publication is in force, withdrawn when only withdrawn ones exist. */
export function deliveryRows(revs: Revision[], pubs: PublicationView[]): DeliveryRow[] {
  return revs.map((rev) => {
    const mine = pubs.filter((p) => p.revisionId === rev.id);
    const active = mine.filter((p) => !p.withdrawnAt);
    const withdrawn = mine.filter((p) => p.withdrawnAt);
    const state: DeliveryState = active.length ? 'delivered' : withdrawn.length ? 'withdrawn' : 'frozen';
    return { rev, active, withdrawn, state };
  });
}

export interface Checks {
  passed: boolean;
  count?: number;
  failed?: number;
}

/** Checks from a manifest as text: "27/27 stóðust", "26/27 stóðust", or passed / failed without a count. */
export function checksText(
  c: Checks,
  t: (k: 'checksOf' | 'checksPassed' | 'checksFailed') => string,
): string {
  if (c.count == null) return c.passed ? t('checksPassed') : t('checksFailed');
  const ok = c.passed ? c.count : c.failed != null ? c.count - c.failed : null;
  if (ok == null) return `${t('checksFailed')} (${c.count})`;
  return fill(t('checksOf'), { a: ok, b: c.count });
}

/**
 * The picture in the project header: staff see the newest revision that has a still (their latest work), others the
 * still of the delivery in force.
 */
export function headStill(
  revs: Pick<Revision, 'stillUrl'>[] | undefined,
  latest: Pick<PublicationView, 'stillUrl'> | null,
): string | null {
  return revs?.find((r) => r.stillUrl)?.stillUrl ?? latest?.stillUrl ?? null;
}
